import React, { useMemo } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  DataTableContainer,
  ACTIONS_COLUMN_ICON,
  ACTIONS_COLUMN_TEXT,
  ACTIONS_VIEW_ICON_SX,
  FilteredColumnsPanel,
  STANDARD_GRID_PROPS,
  STANDARD_COLUMN_HEADER_TITLE_SX,
  DataGridActionsLinkButton,
} from "@app.automotus.io/components/DataTableContainer";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DataGridPro,
  getGridNumericOperators,
  getGridStringOperators,
  GridActionsColDef,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterInputValueProps,
  GridFilterModel,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import {
  GET_INVOICES,
  GetInvoicesData,
  GetInvoicesVars,
  GetInvoicesWhereFilter,
  InvoiceOrderBy,
  InvoiceSummary,
} from "common/graphql";
import { useApolloClient, useQuery } from "@apollo/client";
import { TableErrorOverlay } from "@app.automotus.io/components/PayeeTables/TableErrorOverlay";
import { TableLoadingOverlay } from "@app.automotus.io/components/PayeeTables/TableLoadingOverlay";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Badge from "@mui/material/Badge";
import subDays from "date-fns/subDays";
import addDays from "date-fns/addDays";
import { PayeeTableToolbar } from "@app.automotus.io/components/PayeeTables/PayeeTableToolbar";
import { useFeatureFlags } from "@app.automotus.io/components/hooks";
import { FEATURE_FLAGS } from "@curbsuite/constants";
import { usePendingInvoiceReviewCount } from "@curbsuite/routes/dashboard/invoices/usePendingInvoiceReviewCount";
import { InvoiceReviewStatus } from "common/graphql/fragments/invoiceFragment";
import { formatCurrency } from "common/format";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import format from "date-fns/format";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import { parseSortModel, serializeSortModel } from "common/graphql/queryString";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";

export interface InvoicesLocationState {
  invoiceTablePage?: number;
  invoiceTablePageSize?: number;
  invoiceTableSortStr?: string;
}

const INVOICE_TABLE_DEFAULT_VISIBILITY: GridColumnVisibilityModel = {
  index: true,
  licensePlate: true,
  noticeDate: true,
  paidDate: true,
  status: true,
  amount: true,
  actions: true,
};

const DEFAULT_PAGE_SIZE = 50;

export type InvoiceStatus = "Paid" | "Under Review" | "Void" | "Overdue" | "Mailed" | "Pending";

export interface InvoiceStatusRow {
  paymentTransaction: InvoiceSummary["paymentTransaction"];
  amountPaid: InvoiceSummary["amountPaid"];
  dispute: InvoiceSummary["dispute"];
  voidedAt: InvoiceSummary["voidedAt"];
  noticeSentAt: InvoiceSummary["noticeSentAt"];
  dueAt: InvoiceSummary["dueAt"];
}

// Extracted from the "status" column's valueGetter (AUT-5318 review) so the
// dueAt-nullability branch can be unit tested directly — DataGrid Pro
// virtualizes rows, so individual cell values aren't reachable in jsdom.
export function getInvoiceStatus(row: InvoiceStatusRow): InvoiceStatus {
  const parsedDueAt = row?.dueAt ? new Date(row.dueAt) : null;
  const dueAt = parsedDueAt && parsedDueAt.getUTCFullYear() > 1970 ? parsedDueAt : null;

  if (row.paymentTransaction || row.amountPaid > 0) {
    return "Paid";
  } else if (row?.dispute?.status === "pending") {
    return "Under Review";
  } else if (row.voidedAt) {
    return "Void";
  } else if (row.noticeSentAt && dueAt !== null && new Date(dueAt).getTime() < Date.now()) {
    return "Overdue";
  } else if (row.noticeSentAt) {
    return "Mailed";
  } else {
    return "Pending";
  }
}

export const Invoices: React.FC = () => {
  const { isEnabled } = useFeatureFlags();
  const invoiceReviewEnabled = isEnabled(FEATURE_FLAGS.INVOICE_REVIEW_IS_ENABLED);
  const [now] = React.useState(new Date());
  const [searchParams, setSearchParams] = useSearchParams({
    page: "0",
    pageSize: `${DEFAULT_PAGE_SIZE}`,
    date: startOfMonth(now).toISOString(),
    tab: invoiceReviewEnabled ? "pending" : "all",
  });
  const navigate = useNavigate();

  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    searchParams.set("page", `${newPage}`);
    setSearchParams(searchParams);
  };

  const pageSize = Number(searchParams.get("pageSize") || DEFAULT_PAGE_SIZE);
  const handlePageSizeChange = (newPageSize: number) => {
    searchParams.set("pageSize", `${newPageSize}`);
    setSearchParams(searchParams);
  };

  const variant: "pending" | "all" = invoiceReviewEnabled
    ? (searchParams.get("tab") as "pending" | "all") || "pending"
    : "all";

  const sortStr = searchParams.get("sort") || (variant === "pending" ? "index+asc" : "noticeDate+desc");
  const sortModel: GridSortModel = parseSortModel(sortStr);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    setSearchParams(searchParams);
  };

  const dateStr = searchParams.get("date") ?? startOfMonth(now).toISOString();
  const selectedDate = React.useMemo(() => new Date(dateStr), [dateStr]);
  const handleChangeSelectedDate = (newDate: Date) => {
    searchParams.set("date", newDate.toISOString());
    searchParams.set("page", "0");
    setSearchParams(searchParams, { replace: true });
  };

  const handleChangeVariant = (_event: React.SyntheticEvent, newVariant: "pending" | "all") => {
    if (!newVariant) {
      return;
    }
    searchParams.set("tab", newVariant);
    searchParams.delete("sort");
    searchParams.set("page", "0");
    searchParams.delete("filter");
    setSearchParams(searchParams, { replace: true });
  };

  const handleRowClick = ({ row }: GridRowParams<InvoiceSummary>) => {
    navigate(`${row.id}`, {
      state: {
        invoiceTablePage: page,
        invoiceTablePageSize: pageSize,
        invoiceTableSortStr: sortStr,
      },
    });
  };

  return (
    <Stack spacing={3} width={"100%"} sx={{ height: "100%", flexGrow: 1 }}>
      <Box>
        <Typography variant={"h4"} fontWeight={600}>
          Invoices
        </Typography>
        <Typography variant={"subtitle1"} fontSize={"12px"}>
          Recent invoices can take up to 24 hours to process.
        </Typography>
      </Box>
      <InvoiceSummaryTable
        key={variant}
        showTabs={invoiceReviewEnabled}
        variant={variant}
        onChangeVariant={handleChangeVariant}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onRowClick={handleRowClick}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        selectedDate={selectedDate}
        onChangeSelectedDate={handleChangeSelectedDate}
      />
    </Stack>
  );
};

const InvoiceStatusFilterValue: React.FC<GridFilterInputValueProps> = ({ item, applyValue }) => {
  const handleChange = (event: SelectChangeEvent) => {
    applyValue({ ...item, value: event.target.value as string });
  };

  return (
    <FormControl variant="standard">
      <InputLabel id="status-filter-select-label">Status</InputLabel>
      <Select
        labelId="status-filter-select-label"
        id="status-filter-select"
        value={item.value || ""}
        label="Status"
        onChange={handleChange}
      >
        <MenuItem value={""}>Any</MenuItem>
        <MenuItem value={"Paid"}>Paid</MenuItem>
        <MenuItem value={"Unpaid"}>Unpaid</MenuItem>
        <MenuItem value={"Overdue"}>Overdue</MenuItem>
        <MenuItem value={"Void"}>Void</MenuItem>
        <MenuItem value={"Pending"}>Pending</MenuItem>
        <MenuItem value={"Mailed"}>Mailed</MenuItem>
      </Select>
    </FormControl>
  );
};

interface InvoiceTableToolbarProps {
  showTabs: boolean;
  variant: "pending" | "all";
  onChangeVariant: (event: React.SyntheticEvent, newValue: "pending" | "all") => void;
  pendingInvoiceReviewCount?: number;
  selectedDate: Date;
  onChangeSelectedDate: (d: Date) => void;
  maxDate: Date;
  fetchTotalCount: () => Promise<number>;
  fetchDataBatch: (offset: number, limit: number) => Promise<unknown[]>;
  exportFileName: string;
  columns: GridColDef[];
  defaultVisibilityModel: GridColumnVisibilityModel;
  isIconOnly?: boolean;
  resetLayout: () => void;
}

const InvoiceTableToolbar: React.FC<InvoiceTableToolbarProps> = ({
  showTabs,
  variant,
  onChangeVariant,
  pendingInvoiceReviewCount,
  ...rest
}) => {
  if (!showTabs) {
    return <PayeeTableToolbar {...rest} dateFormat="MMMM yyyy" batchSize={100} />;
  }
  return (
    <Stack>
      <Tabs value={variant} variant="fullWidth" centered onChange={onChangeVariant}>
        <Tab
          value="pending"
          label={
            <Badge badgeContent={pendingInvoiceReviewCount || undefined} color="primary" max={99}>
              <Box component="span" sx={{ pr: pendingInvoiceReviewCount ? 2 : 0 }}>
                Pending Invoices
              </Box>
            </Badge>
          }
        />
        <Tab value="all" label="All Invoices" />
      </Tabs>
      <PayeeTableToolbar {...rest} showDatePicker={variant === "all"} dateFormat="MMMM yyyy" batchSize={100} />
    </Stack>
  );
};

export const InvoiceSummaryTable: React.FC<InvoiceSummaryTableProps> = ({
  showTabs,
  variant,
  onChangeVariant,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  onRowClick,
  sortModel,
  onSortModelChange,
  selectedDate,
  onChangeSelectedDate,
}) => {
  const maxDate = React.useMemo(() => new Date(), []);
  const client = useApolloClient();
  const pendingInvoiceReviewCount = usePendingInvoiceReviewCount();
  const INVOICE_TABLE_COLUMNS = useMemo(
    () => [
      {
        field: "index",
        headerName: "Invoice Number",
        flex: 1,
        align: "left",
        headerAlign: "left",
        valueFormatter({ value }) {
          return `${value}`.padStart(6, "0");
        },
        sortingOrder: ["desc", "asc"],
        filterOperators: getGridNumericOperators().filter((operator) => operator.value === "="),
      } as GridColDef<InvoiceSummary, number, string>,
      {
        field: "licensePlate",
        headerName: "License Plate",
        flex: 1,
        valueGetter({ row }) {
          return `${row.registeredVehicle.licensePlateStateCode} ${row.registeredVehicle.licensePlateNumber}`;
        },
        sortable: false,
        filterOperators: getGridStringOperators().filter((operator) => operator.value === "equals"),
      } as GridColDef<InvoiceSummary, string>,
      ...(variant === "pending"
        ? []
        : [
            {
              field: "noticeDate",
              headerName: "Notice Date",
              type: "date",
              flex: 1,
              valueGetter({ row }) {
                return row.noticeSentAt ? new Date(row.noticeSentAt) : null;
              },
              valueFormatter({ value }) {
                return value ? format(value, "MM/dd/yyyy") : "-";
              },
              sortingOrder: ["desc", "asc"],
              filterable: false,
            } as GridColDef<InvoiceSummary, Date | null, string>,
            {
              field: "paidDate",
              headerName: "Paid Date",
              type: "date",
              flex: 1,
              valueGetter({ row }) {
                return row.paymentTransaction
                  ? new Date(row.paymentTransaction.createdAt)
                  : row.amountPaid > 0
                  ? new Date(row.updatedAt)
                  : null;
              },
              valueFormatter({ value }) {
                return value ? format(value, "MM/dd/yyyy") : "";
              },
              sortingOrder: ["desc", "asc"],
              filterable: false,
            } as GridColDef<InvoiceSummary, Date | null, string>,
          ]),
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        valueGetter({ row }) {
          return getInvoiceStatus(row);
        },
        sortable: false,
        filterable: variant === "all",
        filterOperators: getGridStringOperators()
          .filter((operator) => operator.value === "equals")
          .map((operator) => ({
            ...operator,
            InputComponent: operator.InputComponent ? InvoiceStatusFilterValue : undefined,
          })),
      } as GridColDef<InvoiceSummary, string>,
      {
        field: "amount",
        headerName: "Amount",
        flex: 1,
        sortable: false,
        valueGetter({ row }) {
          return row.transactionAmountDue + row.adminFeeDue - row.adminFeeAmountWaived;
        },
        valueFormatter({ value }) {
          return `$${formatCurrency(value)}`;
        },
        filterable: false,
      } as GridColDef<InvoiceSummary, number, string>,
      {
        ...(variant === "pending" ? ACTIONS_COLUMN_TEXT : ACTIONS_COLUMN_ICON),
        filterable: false,
        getActions: (params) =>
          variant === "pending"
            ? [
                <DataGridActionsLinkButton key="review" to={`${params.row.id}`}>
                  Review
                </DataGridActionsLinkButton>,
              ]
            : [
                <DataGridActionsLinkButton
                  key="view"
                  to={`${params.row.id}`}
                  label="View Invoice"
                  icon={<ArrowForwardIosIcon sx={ACTIONS_VIEW_ICON_SX} />}
                />,
              ],
      } as GridActionsColDef,
    ],
    [variant],
  );
  const offset = page * pageSize;
  const limit = pageSize;

  const parseOrderBy = (sortModel: GridSortModel): InvoiceOrderBy[] => {
    const { field: sortField, sort: sortDirection } = sortModel[0];
    switch (sortField) {
      case "index":
        return [{ index: sortDirection || "asc" }];
      case "paidDate":
        return [
          { payment_transaction: { created_at: sortDirection === "asc" ? "asc_nulls_last" : "desc_nulls_last" } },
        ];
      default:
        return [{ notice_sent_at: sortDirection || "desc" }];
    }
  };

  const pendingDateRange = React.useMemo(() => {
    const since = subDays(new Date(), 60);
    const until = addDays(new Date(), 1);
    return { _gte: since.toISOString(), _lt: until.toISOString() };
  }, []);

  const issuedDateRange = React.useMemo(() => {
    const since = startOfMonth(selectedDate);
    const until = addMonths(since, 1);
    return { _gte: since.toISOString(), _lt: until.toISOString() };
  }, [selectedDate]);

  const [columnFilter, setColumnFilter] = React.useState<Omit<GetInvoicesWhereFilter, "created_at" | "review_status">>(
    {},
  );

  const filter: GetInvoicesWhereFilter = React.useMemo(() => {
    if (variant === "pending") {
      return { review_status: { _eq: InvoiceReviewStatus.PENDING }, created_at: pendingDateRange, ...columnFilter };
    }
    if (showTabs) {
      // Flag ON — "All" tab: non-pending invoices, date range on created_at
      return { review_status: { _neq: InvoiceReviewStatus.PENDING }, created_at: issuedDateRange, ...columnFilter };
    }
    // Flag OFF — original behavior: filter by notice_sent_at date range, which
    // implicitly excludes invoices with no notice and matches what the date picker shows.
    const { notice_sent_at: noticeColumnFilter, ...otherColumnFilter } = columnFilter;
    if (noticeColumnFilter?._is_null === true) {
      // "Pending" status filter: show unmailed invoices — date range does not apply
      return { notice_sent_at: { _is_null: true }, ...otherColumnFilter };
    }
    return { notice_sent_at: { ...issuedDateRange, ...(noticeColumnFilter ?? {}) }, ...otherColumnFilter };
  }, [variant, showTabs, pendingDateRange, issuedDateRange, columnFilter]);

  const onFilterChange = React.useCallback((newFilterModel: GridFilterModel) => {
    const newFilter: Omit<GetInvoicesWhereFilter, "created_at" | "review_status"> = newFilterModel.items.reduce(
      (acc, item) => {
        if (item.columnField === "index") {
          return { ...acc, index: { _eq: item.value } };
        }

        if (item.columnField === "licensePlate") {
          return { ...acc, registered_vehicle: { license_plate: { _eq: String(item.value).toUpperCase() } } };
        }

        if (item.columnField === "status") {
          switch (item.value) {
            case "Paid":
              return { ...acc, amount_paid: { _gt: 0 }, voided_at: { _is_null: true } };
            case "Unpaid":
              return {
                ...acc,
                _not: { _or: [{ payment_transaction: {} }, { amount_paid: { _gt: 0 } }] },
                voided_at: { _is_null: true },
              };
            case "Void":
              return { ...acc, voided_at: { _is_null: false } };
            case "Overdue":
              return {
                ...acc,
                notice_sent_at: { _is_null: false },
                voided_at: { _is_null: true },
                _not: { _or: [{ payment_transaction: {} }, { amount_paid: { _gt: 0 } }] },
                due_at: { _lt: new Date().toISOString() },
              };
            case "Mailed":
              return {
                ...acc,
                notice_sent_at: { _is_null: false },
                voided_at: { _is_null: true },
                _not: { _or: [{ payment_transaction: {} }, { amount_paid: { _gt: 0 } }] },
              };
            case "Pending":
              return {
                ...acc,
                notice_sent_at: { _is_null: true },
                voided_at: { _is_null: true },
                _not: { _or: [{ payment_transaction: {} }, { amount_paid: { _gt: 0 } }] },
              };
            default:
              return acc;
          }
        }

        return acc;
      },
      {} as Omit<GetInvoicesWhereFilter, "review_status">,
    );

    setColumnFilter(newFilter);
  }, []);

  const { data, loading, error, refetch } = useQuery<GetInvoicesData, GetInvoicesVars>(GET_INVOICES, {
    variables: {
      offset,
      limit,
      orderBy: parseOrderBy(sortModel),
      filter,
    },
    fetchPolicy: "network-only",
    onError(err) {
      console.error("failed to fetch invoices", err);
    },
  });

  const fetchTotalCount = React.useCallback(async () => {
    const result = await client.query<GetInvoicesData, GetInvoicesVars>({
      query: GET_INVOICES,
      fetchPolicy: "network-only",
      variables: { offset: 0, limit: 0, orderBy: parseOrderBy(sortModel), filter },
    });
    return result.data?.invoicesAggregate.aggregate.count || 0;
  }, [client, sortModel, filter]);

  const fetchDataBatch = React.useCallback(
    async (batchOffset: number, batchLimit: number) => {
      const result = await client.query<GetInvoicesData, GetInvoicesVars>({
        query: GET_INVOICES,
        fetchPolicy: "network-only",
        variables: { offset: batchOffset, limit: batchLimit, orderBy: parseOrderBy(sortModel), filter },
      });
      return result.data?.invoices || [];
    },
    [client, sortModel, filter],
  );

  const [exportFileName] = React.useState(() => `invoices_${(Math.random() * 1e6).toFixed(0)}`);

  return (
    <DataTableContainer
      // Scoped by variant, not just account — the "pending" and "all" tabs have different columns.
      tableName={`invoices-table-${variant}`}
      defaultVisibilityModel={INVOICE_TABLE_DEFAULT_VISIBILITY}
    >
      {({ apiRef, initialState, isIconOnly, resetLayout }) => (
        <DataGridPro
          apiRef={apiRef}
          localeText={isIconOnly ? { toolbarFilters: "" } : undefined}
          components={{
            ErrorOverlay: TableErrorOverlay,
            LoadingOverlay: TableLoadingOverlay,
            Toolbar: InvoiceTableToolbar,
            ColumnsPanel: FilteredColumnsPanel,
          }}
          componentsProps={{
            toolbar: {
              showTabs,
              variant,
              onChangeVariant,
              pendingInvoiceReviewCount,
              selectedDate,
              onChangeSelectedDate,
              maxDate,
              exportFileName,
              columns: INVOICE_TABLE_COLUMNS,
              fetchTotalCount,
              fetchDataBatch,
              defaultVisibilityModel: INVOICE_TABLE_DEFAULT_VISIBILITY,
              isIconOnly,
              resetLayout,
            },
            errorOverlay: {
              onTryAgain: () => refetch(),
            },
            loadingOverlay: {
              numColumns: INVOICE_TABLE_COLUMNS.length,
            },
          }}
          columns={INVOICE_TABLE_COLUMNS}
          rows={data?.invoices || []}
          rowCount={data?.invoicesAggregate.aggregate.count || 0}
          loading={loading}
          {...STANDARD_GRID_PROPS}
          error={error}
          pagination
          page={page}
          onPageChange={onPageChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          paginationMode={"server"}
          rowsPerPageOptions={[50, 100]}
          onRowClick={onRowClick}
          sortingMode={"server"}
          sortModel={sortModel}
          filterMode={"server"}
          onFilterModelChange={onFilterChange}
          initialState={{
            // pinnedColumns has no controlled-prop equivalent (unlike sortModel/filterModel
            // above), so it must be layered on unconditionally rather than only in a default.
            ...initialState,
            pinnedColumns: variant === "pending" ? { right: ["actions"] } : undefined,
          }}
          onSortModelChange={onSortModelChange}
          sx={{
            flexGrow: 1,
            ...STANDARD_COLUMN_HEADER_TITLE_SX,
            "& .MuiDataGrid-pinnedColumns--right": {
              boxShadow: "none",
            },
            "& .MuiDataGrid-pinnedColumnHeaders--right": {
              boxShadow: "none",
            },
          }}
        />
      )}
    </DataTableContainer>
  );
};

export interface InvoiceSummaryTableProps {
  showTabs: boolean;
  variant: "pending" | "all";
  onChangeVariant: (event: React.SyntheticEvent, newValue: "pending" | "all") => void;
  page: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  onRowClick: (params: GridRowParams<InvoiceSummary>) => void;
  sortModel: GridSortModel;
  onSortModelChange: (sortModel: GridSortModel) => void;
  selectedDate: Date;
  onChangeSelectedDate: (d: Date) => void;
}

export default Invoices;
